'use client';

import { useState } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowIcon, GlowTextBody, Icons, PropsWithClassName } from '../../..';

export type StarRatingProps = PropsWithClassName & {
    totalStars?: number;
    label?: string;
    id?: string;
    name?: string;
    defaultValue?: number;
    onChange?: (rating: number) => void;
};

export const StarRating: Component<StarRatingProps> = ({
    totalStars = 5,
    onChange,
    label,
    id,
    name,
    className,
    defaultValue = 0,
}) => {
    const [rating, setRating] = useState(defaultValue);
    const [hoverRating, setHoverRating] = useState(0);

    const handleRatingChange = (selectedRating: number) => {
        setRating(selectedRating);
        if (onChange) {
            onChange(selectedRating);
        }
    };

    return (
        <div className={clsx('flex flex-row items-center self-stretch gap-1', className)}>
            <GlowTextBody htmlFor={id} fontWeight={'bold'} TagName={'label'} text={label} color={'primary'} />
            <input type={'hidden'} value={rating} id={id} name={name} className={'hidden'} />

            <div className={'flex items-center'} role={'radiogroup'}>
                {[...Array(totalStars)].map((_, index) => {
                    const starValue = index + 1;
                    const isSelected = (hoverRating || rating) >= starValue;

                    return (
                        <div
                            key={index}
                            onMouseEnter={() => setHoverRating(starValue)}
                            onMouseLeave={() => setHoverRating(0)}
                            tabIndex={0}
                            role={'radio'}
                            aria-checked={rating === starValue}
                        >
                            <label
                                htmlFor={`rating-${starValue}`}
                                className={clsx('cursor-pointer', isSelected && 'rating-selected-star')}
                            >
                                {isSelected ? (
                                    <GlowIcon Icon={Icons.Star} type={'xLarge'} />
                                ) : (
                                    <GlowIcon Icon={Icons.WhiteStar} type={'xLarge'} />
                                )}
                            </label>

                            <input
                                type={'radio'}
                                value={starValue}
                                id={`rating-${starValue}`}
                                name={'rating'}
                                checked={rating === starValue}
                                onChange={() => handleRatingChange(starValue)}
                                className={'absolute opacity-0'}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
